import React from "react";
import {
  EMPTY_CHARACTER,
  getFormattedDateString,
} from "@pulse/shared-components";
import {
  IconButton,
  TableProps,
  TableRowItem,
  Typography,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { Instance } from "mobx-state-tree";
import { StopCircle } from "lucide-react";
import { Stack } from "@mui/material";
import { EmailCycleModel } from "../../../models/EmailCycleModel";
import { StyledEllipsisTypography } from "../../../../../components/StyledEllipsisTypography";
import { RightAlignedChip } from "../../../../../components/chips/RightAlignedChip";
import { EmailCycleStatus } from "@pulse/pulse-rpcs/lib/email-cycle/emailCycleStatus";
import { getEmailCycleStatusChipProps } from "../../../utils/getEmailCycleStatusChipProps";
import { getFormattedDateAndTimeString } from "../../../utils/getFormattedDateAndTimeString";

enum EmailCyclesTableColumn {
  CycleName = "CYCLE_NAME",
  Total = "TOTAL",
  Queued = "QUEUED",
  Sent = "SENT",
  Delivered = "DELIVERED",
  Viewed = "VIEWED",
  Unsent = "UNSENT",
  Bounces = "BOUNCES",
  Complaints = "COMPLAINTS",
  Status = "STATUS",
  QueuedOn = "QUEUED_ON",
  LastModified = "LAST_MODIFIED",
  ScheduledAt = "SCHEDULED_AT",
  Creator = "CREATOR",
  Actions = "ACTIONS",
}

const COLUMN_WIDTH = {
  CYCLE_NAME: "280px",
  TOTAL: "120px",
  QUEUED: "120px",
  SENT: "120px",
  DELIVERED: "120px",
  VIEWED: "120px",
  UNSENT: "120px",
  BOUNCES: "120px",
  COMPLAINTS: "130px",
  STATUS: "200px",
  QUEUED_ON: "200px",
  LAST_MODIFIED: "210px",
  SCHEDULED_AT: "250px",
  CREATOR: "280px",
  ACTION: "80px",
};

export const getEmailCyclesTableHeader = (
  t: TFunction,
): TableProps["header"] => [
  {
    columnId: EmailCyclesTableColumn.CycleName,
    label: t("emailCycles.cycleName"),
    width: COLUMN_WIDTH.CYCLE_NAME,
  },
  {
    columnId: EmailCyclesTableColumn.Total,
    label: t("common.total"),
    width: COLUMN_WIDTH.TOTAL,
  },
  {
    columnId: EmailCyclesTableColumn.Queued,
    label: t("emailCycles.queued"),
    width: COLUMN_WIDTH.QUEUED,
  },
  {
    columnId: EmailCyclesTableColumn.Sent,
    label: t("emailCycles.sent"),
    width: COLUMN_WIDTH.SENT,
  },
  {
    columnId: EmailCyclesTableColumn.Delivered,
    label: t("emailCycles.delivered"),
    width: COLUMN_WIDTH.DELIVERED,
  },
  {
    columnId: EmailCyclesTableColumn.Viewed,
    label: t("surveyLinkStatus.viewed"),
    width: COLUMN_WIDTH.VIEWED,
  },
  {
    columnId: EmailCyclesTableColumn.Unsent,
    label: t("emailCycles.unsent"),
    width: COLUMN_WIDTH.UNSENT,
  },
  {
    columnId: EmailCyclesTableColumn.Bounces,
    label: t("emailCycles.bounces"),
    width: COLUMN_WIDTH.BOUNCES,
  },
  {
    columnId: EmailCyclesTableColumn.Complaints,
    label: t("emailCycles.complaints"),
    width: COLUMN_WIDTH.COMPLAINTS,
  },
  {
    columnId: EmailCyclesTableColumn.Status,
    label: t("emailCycles.status"),
    width: COLUMN_WIDTH.STATUS,
  },
  {
    columnId: EmailCyclesTableColumn.ScheduledAt,
    label: t("emailCycles.scheduledAt"),
    width: COLUMN_WIDTH.SCHEDULED_AT,
  },
  {
    columnId: EmailCyclesTableColumn.QueuedOn,
    label: t("emailCycles.queuedOn"),
    width: COLUMN_WIDTH.QUEUED_ON,
    sortable: true,
  },
  {
    columnId: EmailCyclesTableColumn.LastModified,
    label: t("emailCycles.lastModified"),
    width: COLUMN_WIDTH.LAST_MODIFIED,
    sortable: true,
  },
  {
    columnId: EmailCyclesTableColumn.Creator,
    label: t("emailCycles.creator"),
    width: COLUMN_WIDTH.CREATOR,
  },
  {
    columnId: EmailCyclesTableColumn.Actions,
    label: EMPTY_CHARACTER,
    width: COLUMN_WIDTH.ACTION,
  },
];

export const getEmailCyclesTableCells = (
  t: TFunction,
  typography: Typography,
  emailCycle: Instance<typeof EmailCycleModel>,
  onStopEmailCycleClick: (cycleId: string) => void,
  isProcessEmailCyclePrivilegeGranted: boolean,
): TableRowItem["cells"] => {
  const tableCells = [
    {
      columnId: EmailCyclesTableColumn.CycleName,
      data: (
        <StyledEllipsisTypography
          data={emailCycle.cycleName.name}
          typography={typography}
          t={t}
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Total,
      data: (
        <StyledEllipsisTypography
          data={emailCycle.deliveryStats.total}
          typography={typography}
          t={t}
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Queued,
      data: (
        <RightAlignedChip
          label={`${emailCycle.deliveryStats.queued}`}
          color="neutral"
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Sent,
      data: (
        <RightAlignedChip
          label={`${emailCycle.deliveryStats.sent}`}
          color="primarySubtle"
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Delivered,
      data: (
        <RightAlignedChip
          label={`${emailCycle.deliveryStats.delivered}`}
          color="blueSubtle"
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Viewed,
      data: (
        <RightAlignedChip
          label={`${emailCycle.deliveryStats.viewed}`}
          color="greenSubtle"
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Unsent,
      data: (
        <RightAlignedChip
          label={`${emailCycle.deliveryStats.unsent + emailCycle.deliveryStats.optOut}`}
          color="orangeSubtle"
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Bounces,
      data: (
        <RightAlignedChip
          label={`${emailCycle.deliveryStats.bounces}`}
          color="redSubtle"
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Complaints,
      data: (
        <RightAlignedChip
          label={`${emailCycle.deliveryStats.complaints}`}
          color="redSubtle"
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Status,
      data: (
        <RightAlignedChip
          {...getEmailCycleStatusChipProps(t, emailCycle.status)}
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.ScheduledAt,
      data: (
        <StyledEllipsisTypography
          data={getFormattedDateAndTimeString(new Date(emailCycle.scheduledAt))}
          typography={typography}
          t={t}
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.QueuedOn,
      data: (
        <StyledEllipsisTypography
          data={getFormattedDateString(new Date(emailCycle.createdAt))}
          typography={typography}
          t={t}
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.LastModified,
      data: (
        <StyledEllipsisTypography
          data={getFormattedDateString(new Date(emailCycle.modifiedAt))}
          typography={typography}
          t={t}
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Creator,
      data: (
        <StyledEllipsisTypography
          data={emailCycle.createdBy}
          typography={typography}
          t={t}
        />
      ),
    },
    {
      columnId: EmailCyclesTableColumn.Actions,
      data:
        isProcessEmailCyclePrivilegeGranted &&
        emailCycle.status === EmailCycleStatus.EmailCycleStatus.IN_PROGRESS ? (
          <Stack direction="row" justifyContent="end" width="100%">
            <IconButton
              name="stopEmailCycleButton"
              size="small"
              icon={<StopCircle />}
              variant="plain-color"
              color="destructive"
              onClick={(): void => onStopEmailCycleClick(emailCycle.id)}
            />
          </Stack>
        ) : (
          <></>
        ),
    },
  ];

  return tableCells;
};
